import { googleSignInWeb } from "@/lib/authGoogle.web";
import { useOwnerAuth } from "@/components/owner/OwnerAuthProvider";
import { getSafeReturnTo } from "@/lib/navigation/publicReturnTo";
import { router, useLocalSearchParams } from "expo-router";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ActivityIndicator,
  Alert,
  Pressable,
  Text,
  View,
} from "react-native";

export default function SignInWeb() {
  const { user, ownerReady, ownerError } = useOwnerAuth();
  const params = useLocalSearchParams<{ returnTo?: string | string[] }>();
  const [busy, setBusy] = useState(false);
  const routedRef = useRef(false);
  const returnTo = useMemo(
    () => getSafeReturnTo(params.returnTo) || "/(owner)/start",
    [params.returnTo]
  );

  useEffect(() => {
    if (!user || !ownerReady || routedRef.current) return;
    routedRef.current = true;
    router.replace(returnTo as any);
  }, [user, ownerReady, returnTo]);

  useEffect(() => {
    if (!ownerError) return;
    Alert.alert(
      "Owner account needs attention",
      "We could not safely prepare your owner account. Retry, or contact support if this continues."
    );
  }, [ownerError]);

  const handleGoogleSignInWeb = useCallback(async () => {
    if (busy) return;

    try {
      setBusy(true);

      await googleSignInWeb();
    } catch (error: any) {
      console.warn("Google web login failed.");

      Alert.alert(
        "Google login failed",
        error?.message ??
          "Google login failed. Please check your Firebase web Google login configuration."
      );
    } finally {
      setBusy(false);
    }
  }, [busy]);

  const handleContinue = useCallback(() => {
    void handleGoogleSignInWeb();
  }, [handleGoogleSignInWeb]);

  return (
    <View
      style={{
        flex: 1,
        padding: 16,
        justifyContent: "center",
        gap: 12,
        backgroundColor: "#ffffff",
      }}
    >
      <Text
        style={{
          fontSize: 22,
          fontWeight: "900",
          color: "#111827",
        }}
      >
        Login
      </Text>

      <Text
        style={{
          fontSize: 14,
          color: "#6b7280",
          lineHeight: 20,
        }}
      >
        Continue with Google to create or manage your Soko Grid owner account.
      </Text>

      <Pressable
        disabled={busy}
        onPress={handleContinue}
        style={{
          height: 48,
          borderRadius: 14,
          backgroundColor: busy ? "#9ca3af" : "#111827",
          alignItems: "center",
          justifyContent: "center",
          opacity: busy ? 0.7 : 1,
          flexDirection: "row",
          gap: 8,
        }}
      >
        {busy ? <ActivityIndicator color="#ffffff" /> : null}

        <Text style={{ color: "#fff", fontWeight: "900" }}>
          {busy ? "Preparing account..." : "Continue with Google"}
        </Text>
      </Pressable>
    </View>
  );
}
